/**
 *  Main purpose of this module is to separate the data
 *  entered by the user on login screen (which will be
 *  validated and may be invalid) from keys used throughout
 *  the rest of the application.
 *  It is a buffer, so when user successfully logs in,
 *  it is cleared.
 *  There are 2 API keys, one for the map API (apiKey)
 *  and second key for the panorama API (momaKey).
 *  `MoMa` is an abbreviation for `MObile MApping`.
 */
import { stringify } from 'qs';
import i18n from '../../translations';

const MOMA_URL = process.env.VUE_APP_MOMA_API_URL;
const MAP_URL = process.env.VUE_APP_MAP_API_URL;

const getters = {
    /**
     * Getter function returning maps key (apiKey) value.
     * @type {string}
     */
    apiKey: ({ apiKey }) => apiKey,

    /**
     * Getter function returning apiError, error connected to apiKey (maps api).
     * @type {string}
     */
    apiError: ({ apiError }) => apiError,

    /**
     * Getter function returning panorama key (momaKey) value.
     * @type {string}
     */
    momaKey: ({ momaKey }) => momaKey,

    /**
     * Getter function returning momaError, error connected to momaKey (panorama api).
     * @type {string}
     */
    momaError: ({ momaError }) => momaError,

    /**
     * Getter that disables button when user does not have both keys
     * or has errors.
     * @type {boolean}
     */
    loginDisabled: ({
        apiKey,
        apiError,
        momaKey,
        momaError,
    }) => !(apiKey && momaKey) || (apiError || momaError),
};

/**
 * Names of mutations further used by their variable names, not values.
 * E.g. first mutation is referred by `[SET_API_KEY]`, not `'setApiKey'`.
 */
const mutationNames = {
    SET_API_KEY: 'setApiKey',
    SET_API_ERROR: 'setApiError',
    SET_MOMA_KEY: 'setMomaKey',
    SET_MOMA_ERROR: 'setMomaError',
    CLEAR_KEYS: 'clearKeys',
    SET_VERIFYING_API_KEY: 'setVerifyingApiKey',
    SET_VERIFYING_MOMA_KEY: 'setVerifyingMomaKey',
};

const mutations = {
    /**
     * Setter mutation for maps key (apiKey).
     * @param {string} key maps API key
     */
    [mutationNames.SET_API_KEY]: (state, key) => {
        state.apiKey = key;
    },

    /**
     * Setter mutation for maps key error (apiError).
     * @param {string} error maps API key error message
     */
    [mutationNames.SET_API_ERROR]: (state, error) => {
        state.apiError = error;
    },

    /**
     * Setter mutation for panorama key (momaKey).
     * @param {string} key MoMa (panorama) key
     */
    [mutationNames.SET_MOMA_KEY]: (state, key) => {
        state.momaKey = key;
    },

    /**
     * Setter mutation for panorama key error (momaError).
     * @param {string} error MoMa key error message
     */
    [mutationNames.SET_MOMA_ERROR]: (state, error) => {
        state.momaError = error;
    },

    /**
     * Mutation clearing both keys: map key (apiKey) and panorama key (momaKey).
     */
    [mutationNames.CLEAR_KEYS]: (state) => {
        state.apiKey = null;
        state.momaKey = null;
    },

    /**
     * Sets flag indicating that the API key is being verified with
     * TomTom API.
     * @params {boolean} flag The current state of verification, `true`
     *     when in progress, `false` otherwise.
     */
    [mutationNames.SET_VERIFYING_API_KEY]: (state, flag) => {
        state.isVerifyingApiKey = flag;
    },

    /**
     * Sets flag indicating that the MoMa key is being verified with
     * TomTom API.
     * @params {boolean} flag The current state of verification, `true`
     *     when in progress, `false` otherwise.
     */
    [mutationNames.SET_VERIFYING_MOMA_KEY]: (state, flag) => {
        state.isVerifyingMomaKey = flag;
    },
};

/**
 * State consists of 4 variables storing 2 string keys and 2 errors for
 * them:
 */
const state = {
    /**
     * Maps API key
     * @type {string}
     */
    apiKey: null,
    /**
     * Maps API login error
     * @type {string}
     */
    apiError: null,
    /**
     * Flag indicating if API key is being verified with TomTom API at
     * the moment.
     * @type {boolean}
     */
    isVerifyingApiKey: false,

    /**
     * Panorama view API key
     * @type {string}
     */
    momaKey: null,
    /**
     * Panorama view API login error
     * @type {string}
     */
    momaError: null,
    /**
     * Flag indicating if Moma key is being verified with TomTom API at
     * the moment.
     * @type {boolean}
     */
    isVerifyingMomaKey: false,
};

/**
 * Asynchronous method used to set api or moma key and check
 * if entered key is correct. It connects with TomTom API to check the key.
 * For the period of verification the verification flag is set to `true`.
 * @param {string} keyMutation Name of mutation used to set given key.
 * @param {string} key API key value.
 * @param {string} verificationMutation Name of mutation used to set verification
 * state.
 * @param {string} errorAction Name of action dispatched after error.
 * @param {string} url TomTom api sub-address (to check key validity).
 */
async function setAndValidate({
    commit, dispatch,
    keyMutation, key,
    verificationMutation,
    errorAction,
    url,
}) {
    commit(keyMutation, key);

    if (!key || !key.length) {
        dispatch(errorAction, i18n.t('message.isNeeded'));
        return;
    }

    commit(verificationMutation, true);

    try {
        const queryString = stringify({
            key,
            service: 'WMS',
            request: 'GetCapabilities',
        });

        const { ok } = await fetch(
            `${url}?${queryString}`,
        );

        dispatch(errorAction, ok ? null : i18n.t('message.inValidApiKey'));
    } catch {
        dispatch(errorAction, i18n.t('message.inValidPanoramaKey'));
    }

    commit(verificationMutation, false);
}

const actions = {
    /**
     * Vuex action for setting maps API key.
     * Uses `setAndValidate` function to set key value.
     * Passes custom mutation names to set apiKey and
     * possible errors regarding this key. Also
     * passes mutation name for setting the verification flag.
     * @param {string} key maps API key value
     */
    setApiKey: (context, key) => {
        setAndValidate({
            ...context,
            keyMutation: mutationNames.SET_API_KEY,
            verificationMutation: mutationNames.SET_VERIFYING_API_KEY,
            key,
            errorAction: 'setApiError',
            url: `${MAP_URL}/wms//`,
        });
    },

    /**
     * Action setting given maps api key error message.
     * @param {string} error Maps API key error message
     */
    setApiError: ({ commit }, error) => {
        commit(mutationNames.SET_API_ERROR, error);
    },

    /**
     * Vuex action for setting panorama key.
     * Uses `setAndValidate` function to set key value.
     * Passes custom mutation names to set momaKey and
     * possible errors regarding this key. Also
     * passes mutation name for setting the verification flag.
     * @param {string} key MoMa (panorama) API key value
     */
    setMomaKey: async (context, key) => {
        setAndValidate({
            ...context,
            keyMutation: mutationNames.SET_MOMA_KEY,
            verificationMutation: mutationNames.SET_VERIFYING_MOMA_KEY,
            key,
            errorAction: 'setMomaError',
            url: `${MOMA_URL}/track`,
        });
    },

    /**
     * Action setting given panorama api key error message.
     * @param {string} error MoMa (panorama) API key error message
     */
    setMomaError: ({ commit }, error) => {
        commit(mutationNames.SET_MOMA_ERROR, error);
    },
};

/**
 * Vuex store module object, created by joining
 * actions, getters, mutations and state objects.
 */
export default {
    namespaced: true,
    actions,
    getters,
    mutationNames,
    mutations,
    state,
};
